import React, { useState, useEffect, useContext } from "react";
import AuthContext from "../../../../../contexts/AuthContext";
import CustomTag from "../../../../../ui/CustomTag";
import CustomList from "../../../../../ui/CustomList";
import dayjs from "dayjs";
import { Tab, Tabs, CircularProgress } from "@mui/material";
import { translateZammadState, getZammadStateVariant } from "../../../../../utils";

const CustomerSupport = ({ customer }) => {
    const [tab, setTab] = useState(0);
    const [combinedTickets, setCombinedTickets] = useState({});
    const [isLoaded, setIsLoaded] = useState(false);
    const { token } = useContext(AuthContext);

    useEffect(() => {
        if (customer?.tickets) {
            fetchCombinedTickets();
        }
    }, [customer]);

    const fetchCombinedTickets = () => {
        setIsLoaded(false);

        try {
            const combined = {};

            if (customer?.tickets) {
                Object.keys(customer.tickets).forEach((year) => {
                    if (!combined[year]) combined[year] = [];

                    customer.tickets[year].forEach((ticket) => {
                        if (ticket.source === 'Desk') {
                            const deskHours = ticket.timeEntries?.reduce((total, entry) => total + entry.hoursConsumed, 0) || 0;

                            combined[year].push({
                                ...ticket,
                                source: "Desk",
                                sourceUrl: `${process.env.REACT_APP_DESK_URL}/${ticket.id}`,
                                displaySubject: ticket.subject,
                                displayState: ticket.status === "On Hold" ? "Fait" : "Fermé",
                                displayStateClass: ticket.status === "On Hold" ? "waiting" : "success",
                                displayTime: deskHours,
                                displayCreatedAt: null,
                                displayClosedAt: ticket.closedTime ? dayjs(ticket.closedTime).format("DD/MM/YYYY") : "",
                                closedAtSort: ticket.closedTime ? dayjs(ticket.closedTime).unix() : 0,
                            });
                        } else if (ticket.source === 'Zammad') {
                            const totalHours = ticket.timeEntries
                                ? ticket.timeEntries.reduce((total, entry) => {
                                      const timeUnit = parseFloat(entry.time_unit) || 0;
                                      return total + timeUnit;
                                  }, 0)
                                : 0;

                            combined[year].push({
                                ...ticket,
                                source: "Zammad",
                                sourceUrl: `${process.env.REACT_APP_ZAMMAD_URL}/#ticket/zoom/${ticket.id}`,
                                displaySubject: ticket.subject,
                                displayState: translateZammadState(ticket.state),
                                displayStateClass: getZammadStateVariant(ticket.state),
                                displayTime: totalHours,
                                displayCreatedAt: dayjs(ticket.created_at).format("DD/MM/YYYY"),
                                displayClosedAt: ticket.close_at ? dayjs(ticket.close_at).format("DD/MM/YYYY") : "-",
                                closedAtSort: ticket.close_at ? dayjs(ticket.close_at).unix() : 0,
                            });
                        }
                    });
                });
            }

            setCombinedTickets(combined);

            const years = Object.keys(combined).reverse();
            const currentYearIndex = years.indexOf(String(dayjs().year()));
            if (currentYearIndex !== -1) {
                setTab(currentYearIndex);
            }
        } catch (error) {
            console.error("Error combining tickets:", error);
        } finally {
            setIsLoaded(true);
        }
    };


    const ticketsCols = React.useMemo(
        () => [
            {
                Header: "Source",
                icon: "source",
                accessor: "source",
                Cell: ({ cell: { value } }) => <CustomTag value={value} className={value === "Desk" ? "success" : "premium"} />,
            },
            {
                Header: "Sujet",
                icon: "objet",
                downloadAccessor: "displaySubject",
                Cell: (row) => (
                    <a href={row.row.original.sourceUrl} rel="noreferrer" target="_blank">
                        {row.row.original.displaySubject}
                    </a>
                ),
            },
            {
                Header: "Temps (en h.)",
                icon: "conso",
                accessor: "displayTime",
                Cell: ({ cell: { value } }) => <span>{isNaN(value) ? "0.0" : value.toFixed(1)} h</span>,
            },
            {
                Header: "État",
                icon: "conso",
                accessor: "displayState",
                Cell: ({ row }) => <CustomTag value={row.original.displayState} className={row.original.displayStateClass} />,
            },
            {
                Header: "Créé le",
                accessor: "displayCreatedAt",
                icon: "calendar",
                Cell: ({ cell: { value } }) => <span>{value || "-"}</span>,
            },
            {
                Header: "Fermé le",
                accessor: "closedAtSort",
                sortType: "basic",
                icon: "calendar",
                Cell: ({ row }) => <span>{row.original.displayClosedAt || "-"}</span>,
            },
        ],
        []
    );

    if (!isLoaded) {
        return (
        <div className="loader-container absolute">
            <CircularProgress size={30} />
        </div>
        );
    }

    return (
        <div className="tab-container">
            {Object.keys(combinedTickets).length > 0 ? (
                <div className="tabs-container">
                    <div className="tabs-wrapper">
                        <Tabs value={tab} onChange={(event, value) => setTab(value)}>
                            {Object.keys(combinedTickets)
                                .reverse()
                                .map((year, index) => (
                                    <Tab value={index} key={index} label={`${year} (${combinedTickets[year].length})`} />
                                ))}
                        </Tabs>
                    </div>
                    {Object.values(combinedTickets)
                        .reverse()
                        .map((yearTickets, index) => (
                            <div key={index} role="tabpanel" hidden={tab !== index}>
                                <CustomList entity={yearTickets} columns={ticketsCols} isSelectable={false} isFetchable={false} isClickable={false} isDownloadable limit={20} initialSortBy={[{ id: "closedAtSort", desc: true }]} />
                            </div>
                        ))}
                </div>
            ) : (
                <CustomTag value="Ce client ne possède aucun ticket pour le moment." className="blue" />
            )}
        </div>
    );
};

export default CustomerSupport;
